import React, { useEffect } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import {
  Home as HomeIcon, HeartPulse, FileText, Briefcase, ShieldCheck,
  Check, Clock, ShieldAlert, ArrowRight, ArrowLeft,
} from "lucide-react";
import { PROGRAM_DETAILS } from "../data/content";

const ICONS = { Home: HomeIcon, HeartPulse, FileText, Briefcase };

const ROADMAP = [
  { days: "Days 1 to 30", accent: "border-navy", tone: "text-navy", title: "Phase 1: Stabilization & Healthcare", body: "Immediate shelter security, medical/dental screenings, DD-214 retrieval, and establishing verified sobriety routines." },
  { days: "Days 31 to 90", accent: "border-gold", tone: "text-gold-deep", title: "Phase 2: Claims & Skill Development", body: "Accredited VSO files VA disability claims, resume writing, career interviews, and financial budgeting workshops." },
  { days: "Days 91 to 180", accent: "border-teal", tone: "text-teal", title: "Phase 3: Lease & Reintegration", body: "HUD-VASH or SSVF lease approval, move-in furniture grants, employment stabilization, and monthly follow-up." },
];

const ProgramDetail = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const program = PROGRAM_DETAILS.find((p) => p.id === programId);

  useEffect(() => {
    if (program) document.title = `${program.title} | Iron Shield`;
    window.scrollTo(0, 0);
  }, [program]);

  if (!program) return <Navigate to="/programs" replace />;
  const Icon = ICONS[program.iconName] || ShieldCheck;

  return (
    <div className="min-h-screen bg-offwhite pb-20" data-testid="program-detail-page">
      {/* Header banner */}
      <section className="bg-navy text-offwhite pt-[130px] md:pt-[150px] pb-12 border-b-4 border-gold">
        <div className="container-x">
          <Link to="/programs" data-testid="program-detail-back" className="inline-flex items-center gap-2 text-offwhite/70 hover:text-gold text-xs font-bold uppercase tracking-widest transition-colors">
            <ArrowLeft className="w-4 h-4" /> All Programs
          </Link>
          <div className="mt-5 flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-white/10 flex items-center justify-center text-gold"><Icon className="w-6 h-6" /></div>
            <span className="text-gold text-xs font-bold uppercase tracking-[0.2em]">{program.category} Program</span>
          </div>
          <h1 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">{program.title}</h1>
          <p className="mt-2 text-xs font-bold uppercase tracking-wider text-gold/90">{program.tagline}</p>
        </div>
      </section>

      {/* Detail body */}
      <section className="container-x py-12">
        <div className="bg-white rounded-xl border border-softgray brand-card-shadow p-6 sm:p-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-8 border-b border-softgray">
            <div>
              <span className="px-3 py-1 bg-navy/10 text-navy rounded-md text-xs font-bold uppercase tracking-widest">{program.category} Program</span>
              <h2 className="text-2xl sm:text-4xl font-bold uppercase tracking-tight text-navy mt-3">{program.title}</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate("/admissions")} data-testid="program-detail-apply" className="px-6 py-3.5 bg-navy text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-navy-deep transition-colors flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-gold" /> Apply For Intake
              </button>
              <button onClick={() => navigate("/partners")} data-testid="program-detail-refer" className="px-6 py-3.5 bg-offwhite text-navy border border-navy rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-navy hover:text-white transition-colors">
                Refer A Veteran
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pt-8">
            <div className="lg:col-span-7 space-y-6">
              <div>
                <h3 className="text-lg font-bold text-navy mb-2">Program Overview</h3>
                <p className="text-sm sm:text-base text-charcoal leading-relaxed">{program.description}</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-navy mb-3">Key Features & Services Included</h3>
                <div className="space-y-2.5">
                  {program.highlights.map((item) => (
                    <div key={item} className="flex items-start gap-3 bg-offwhite p-3 rounded-lg border border-softgray">
                      <Check className="w-4 h-4 text-navy flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm text-charcoal font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="bg-navy text-white p-6 rounded-2xl space-y-4" data-testid="program-detail-eligibility">
                <h3 className="text-lg font-bold border-b border-white/10 pb-2 text-gold">Eligibility Snapshot</h3>
                <p className="text-xs text-offwhite/80 leading-relaxed">{program.eligibility}</p>
                <div className="pt-2 border-t border-white/10">
                  <div className="text-xs font-bold text-gold uppercase tracking-wider flex items-center gap-1.5 mb-1">
                    <Clock className="w-4 h-4" /> Duration & Stay Timeline
                  </div>
                  <p className="text-xs text-offwhite/80 font-medium">{program.lengthOfStay}</p>
                </div>
              </div>
              <div className="bg-offwhite p-6 rounded-2xl border border-softgray text-xs text-slate space-y-2">
                <div className="font-bold text-navy text-sm">Strict Zero Cost Policy</div>
                <p>No veteran is ever charged rent or fees for emergency or transitional reset services at Iron Shield.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Roadmap */}
        <div className="bg-white rounded-3xl p-8 border border-softgray mt-8">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h3 className="text-2xl font-bold text-navy">30-90-180 Day Transition Roadmap</h3>
            <p className="text-xs text-slate mt-1">How residents progress through structured phases toward self-sufficiency.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ROADMAP.map((r) => (
              <div key={r.days} className={`p-5 rounded-2xl bg-offwhite border-l-4 ${r.accent}`}>
                <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${r.tone}`}>{r.days}</div>
                <h4 className="font-bold text-navy text-base mb-2">{r.title}</h4>
                <p className="text-xs text-slate leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Other programs */}
        <div className="mt-10">
          <div className="label-caps text-navy mb-4">Continue exploring</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PROGRAM_DETAILS.filter((p) => p.id !== program.id).map((p) => (
              <Link key={p.id} to={`/programs/${p.id}`} data-testid={`program-detail-other-${p.id}`} className="i-card p-4 group hover:border-gold transition-colors">
                <div className="label-caps text-gold text-[10px]">{p.category}</div>
                <div className="mt-1 font-semibold text-navy text-sm leading-snug">{p.title}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-navy group-hover:text-gold-deep"><ArrowRight className="h-3.5 w-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
